import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useIsDesktop } from './useIsDesktop'

function OverviewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}

function AccountsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}

function OfficesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h6" />
    </svg>
  )
}

function ShiftTemplatesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function CodesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12 12 2h8v8L10 20a2 2 0 0 1-2.83 0l-5.17-5.17a2 2 0 0 1 0-2.83Z" />
      <circle cx="15.5" cy="8.5" r="1.5" />
    </svg>
  )
}

function LeaveTypesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

function AuditLogIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  )
}

function ReportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 17v-3M12 17v-6M16 17v-4" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export type AdminArea = 'overview' | 'accounts' | 'teams' | 'shift-templates' | 'codes' | 'leave-types' | 'audit-log' | 'daily-coverage' | 'two-week-manpower' | 'monthly-manpower' | 'leave-availability' | 'detailed-out' | 'seniority-roster' | 'overtime-planning' | 'assignment-sheet' | 'daily-roster' | 'pay-period-schedule'

type NavLink = { id: string; label: string; icon: ReactNode; route: string }
type NavGroup = { label: string; items: NavLink[] }

const OVERVIEW: NavLink = { id: 'overview', label: 'Overview', icon: <OverviewIcon />, route: '/admin' }

/**
 * The full administration map from docs/design/journey-map.md, grouped the
 * same way the dashboard's own summary is. Only built screens are listed: an
 * area joins the navigation when its route exists.
 */
const GROUPS: NavGroup[] = [
  {
    label: 'People',
    items: [
      { id: 'accounts', label: 'Accounts & roles', icon: <AccountsIcon />, route: '/admin/accounts' },
    ],
  },
  {
    label: 'Organization',
    items: [
      { id: 'teams', label: 'Field offices & teams', icon: <OfficesIcon />, route: '/admin/teams' },
      { id: 'shift-templates', label: 'Shift templates', icon: <ShiftTemplatesIcon />, route: '/admin/shift-templates' },
      { id: 'codes', label: 'Codes & activities', icon: <CodesIcon />, route: '/admin/codes' },
      { id: 'leave-types', label: 'Leave types', icon: <LeaveTypesIcon />, route: '/admin/leave-types' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { id: 'daily-roster', label: 'Daily roster', icon: <ReportIcon />, route: '/admin/reports/daily-roster' },
      { id: 'pay-period-schedule', label: 'Pay period schedule', icon: <ReportIcon />, route: '/admin/reports/pay-period-schedule' },
      { id: 'daily-coverage', label: 'Daily coverage', icon: <ReportIcon />, route: '/admin/reports/daily-coverage' },
      { id: 'two-week-manpower', label: 'Two-week manpower', icon: <ReportIcon />, route: '/admin/reports/two-week-manpower' },
      { id: 'monthly-manpower', label: 'Monthly manpower', icon: <ReportIcon />, route: '/admin/reports/monthly-manpower' },
      { id: 'leave-availability', label: 'Leave and availability', icon: <ReportIcon />, route: '/admin/reports/leave-availability' },
      { id: 'detailed-out', label: 'Detailed out', icon: <ReportIcon />, route: '/admin/reports/detailed-out' },
      { id: 'seniority-roster', label: 'Seniority roster', icon: <ReportIcon />, route: '/admin/reports/seniority-roster' },
      { id: 'assignment-sheet', label: 'Daily assignment sheet', icon: <ReportIcon />, route: '/admin/reports/assignment-sheet' },
      { id: 'overtime-planning', label: 'Overtime planning', icon: <ReportIcon />, route: '/admin/reports/overtime-planning' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'audit-log', label: 'Audit log', icon: <AuditLogIcon />, route: '/admin/audit-log' },
    ],
  },
]

/**
 * The administrator workspace's persistent navigation: every area from the
 * journey map, so the shape of the whole console is visible from the first
 * screen. Grows a real route in `route` as each area is built; see
 * docs/product/capabilities.md for what exists today.
 *
 * Static and in flow at the `lg` breakpoint and up; below it, a 260px
 * sidebar left nothing else on a phone width, so it becomes a drawer that
 * opens over the content instead. The parent owns `open` because closing it
 * has to return focus to the button that opened it.
 */
export default function AdminNav({ current, open, onClose }: { current: AdminArea; open: boolean; onClose: () => void }) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const isDesktop = useIsDesktop()

  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    closeButtonRef.current?.focus()

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  return (
    <>
      {open && (
        <div className="absolute inset-0 z-20 bg-ink/40 lg:hidden" onClick={onClose} aria-hidden="true" />
      )}

      <nav
        aria-label="Administration"
        // Below `lg`, a closed drawer is only translated offscreen: its
        // focusable children stay in the tab order unless `inert` removes
        // them from it too. The desktop nav ignores `open` altogether, so
        // this only applies inert there when it is not the static one.
        inert={!isDesktop && !open ? true : undefined}
        className={`absolute inset-y-0 left-0 z-30 w-[260px] flex-none overflow-y-auto border-r border-line bg-canvas py-4 transition-transform duration-200 lg:static lg:translate-x-0 print:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="mb-1 flex items-center justify-between px-3.5 lg:hidden">
          <span className="text-caption font-bold tracking-wide text-ink-subtle uppercase">Administration</span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close administration menu"
            className="flex size-11 items-center justify-center rounded-control text-ink-muted"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="px-3.5">
          <Link
            to={OVERVIEW.route}
            aria-current={current === 'overview' ? 'page' : undefined}
            className={`flex items-center gap-2.5 rounded-control px-3 py-3 text-small font-bold ${
              current === 'overview' ? 'bg-selected text-primary' : 'text-ink-muted hover:bg-surface-2'
            }`}
          >
            <span className="flex size-4.5 items-center justify-center">{OVERVIEW.icon}</span>
            {OVERVIEW.label}
          </Link>
        </div>

        {GROUPS.map((group) => (
          <div key={group.label}>
            <p className="mt-4.5 mb-1.5 px-4 text-caption font-bold tracking-wide text-ink-subtle uppercase">
              {group.label}
            </p>
            <div className="flex flex-col gap-0.5 px-3.5">
              {group.items.map((item) => (
                <Link
                  key={item.id}
                  to={item.route}
                  aria-current={current === item.id ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-control px-3 py-2 text-small font-semibold ${
                    current === item.id ? 'bg-selected text-primary' : 'text-ink-muted hover:bg-surface-2'
                  }`}
                >
                  <span className="flex size-4.5 items-center justify-center">{item.icon}</span>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </>
  )
}
